import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  type SimpleChanges,
  TemplateRef,
  ViewChild,
  inject
} from '@angular/core';

import { FormControl, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  DropdownPosition,
  NgFooterTemplateDirective,
  NgHeaderTemplateDirective,
  NgLabelTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgNotFoundTemplateDirective,
  NgOptgroupTemplateDirective,
  NgOptionComponent,
  NgOptionTemplateDirective,
  NgSelectComponent
} from '@ng-select/ng-select';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { Subject } from 'rxjs';

import { MbsSize, MbsValidators } from '../../utils';

import { NgClass, NgTemplateOutlet } from '@angular/common';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { InputBase } from '../input-base/input-base';
import { InputErrorsComponent } from '../input-base/input-errors/input-errors.component';
import { InputLabelComponent } from '../input-base/input-label/input-label.component';

export declare type AddTagFn = (term: string) => any | Promise<any>;
export declare type CompareWithFn = (a: any, b: any) => boolean;
export declare type GroupValueFn = (key: string | any, children: any[]) => string | any;

export interface SearchState {
  term: string;
  items: any[];
}

let id = 0;

@UntilDestroy()
@Component({
  selector: 'mbs-select',
  templateUrl: './select.component.html',
  imports: [
    CheckboxComponent,
    FormsModule,
    InputErrorsComponent,
    InputLabelComponent,
    NgClass,
    NgFooterTemplateDirective,
    NgHeaderTemplateDirective,
    NgLabelTemplateDirective,
    NgMultiLabelTemplateDirective,
    NgNotFoundTemplateDirective,
    NgOptgroupTemplateDirective,
    NgOptionTemplateDirective,
    NgSelectComponent,
    NgTemplateOutlet,
    ReactiveFormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent extends InputBase<any> implements OnInit, AfterViewInit, OnChanges {
  protected cd: ChangeDetectorRef;

  @Input() id = `mbs-select-${id++}`;
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() markFirst = true;
  @Input() placeholder: string;
  @Input() notFoundText = 'Items not found';
  @Input() typeToSearchText: string;
  @Input() addTagText: string;
  @Input() loadingText: string;
  @Input() clearAllText: string;
  @Input() appearance: string;
  @Input() dropdownPosition: DropdownPosition = 'auto';
  @Input() appendTo: string;
  // Should be .modal-content for placement in modal
  // ng-select resolves appendTo via document.querySelector, which returns the first match in the DOM:
  // with stacked modals '.modal-content' is the bottom one, so the panel would open hidden behind the top modal
  protected get resolvedAppendTo(): string {
    return this.appendTo === '.modal-content' && this.ngbModal.hasOpenModals()
      ? 'ngb-modal-window:last-of-type .modal-content'
      : this.appendTo;
  }
  @Input() loading = false;
  @Input() closeOnSelect = true;
  @Input() hideSelected = false;
  @Input() selectOnTab = false;
  @Input() openOnEnter: boolean;
  @Input() maxSelectedItems: number;
  @Input() groupBy: string | (() => any);
  @Input() bufferAmount = 4;
  @Input() virtualScroll: boolean;
  @Input() selectableGroup = false;
  @Input() selectableGroupAsModel = true;
  @Input() searchFn = null;
  @Input() trackByFn = null;
  @Input() clearOnBackspace = true;
  @Input() labelForId = null;
  @Input() labelForCounter: string;
  @Input() isCounterShowing = false;
  @Input() inputAttrs: { [key: string]: string } = {};
  @Input() tabIndex: number;
  @Input() readonly = false;
  @Input() searchWhileComposing = true;
  @Input() minTermLength = 0;
  @Input() editableSearchTerm = false;
  @Input() keyDownFn = (arg: KeyboardEvent) => true;
  @Input() hideOn: Array<'scroll'> = ['scroll']; // In future possible to add 'blur', 'esc', etc
  @Input() multiple = false;
  @Input() addTag: boolean | AddTagFn = false;
  @Input() searchable = true;
  @Input() clearable = true;
  @Input() isCheckboxSelect = false;
  @Input() isCheckboxOverflow = false;
  @Input() isOpen = null;
  @Input() isAutoWidth = false;

  @Input() itemTemplate: TemplateRef<any>;

  @Input() public required = false;

  @Input() public typeahead: Subject<any>;

  @Input() compareWith?: CompareWithFn;

  @Input() clearSearchOnAdd: boolean;

  @Input() items: any[] = [];
  @Input() selectedItemsFirst = false;
  @Input() clearUnavailableSelection = false;

  /**
   * Add font-weight-bold;
   */
  @Input() public boldLabel = true;

  @Input() hint: TemplateRef<any>;
  @Input() description: TemplateRef<any>;

  @Output('blur') blurEvent = new EventEmitter();
  @Output('focus') focusEvent = new EventEmitter();
  @Output('change') changeEvent = new EventEmitter();
  @Output('open') openEvent = new EventEmitter();
  @Output('close') closeEvent = new EventEmitter();
  @Output('search') searchEvent = new EventEmitter<{ term: string; items: any[] }>();
  @Output('clear') clearEvent = new EventEmitter();
  @Output('add') addEvent = new EventEmitter();
  @Output('remove') removeEvent = new EventEmitter();
  @Output('scroll') scroll = new EventEmitter<{ start: number; end: number }>();
  @Output('scrollToEnd') scrollToEnd = new EventEmitter();

  @ContentChild('mbsSelectFooterTmp', { read: TemplateRef, static: false }) mbsSelectFooterTmp: TemplateRef<any>;
  @ContentChild('mbsSelectLabelTmp', { read: TemplateRef, static: false }) mbsSelectLabelTmp: TemplateRef<any>;
  @ContentChild('mbsSelectOptionTmp', { read: TemplateRef, static: false }) mbsSelectOptionTmp: TemplateRef<any>;
  @ContentChild('mbsSelectMultiLabelTmp', { read: TemplateRef, static: false }) mbsSelectMultiLabelTmp: TemplateRef<any>;
  @ContentChild('mbsSelectOptGroupTmp', { read: TemplateRef, static: false }) mbsSelectOptGroupTmp: TemplateRef<any>;
  @ContentChild('mbsSelectHeaderTmp', { read: TemplateRef, static: false }) mbsSelectHeaderTmp: TemplateRef<any>;
  @ContentChild('mbsSelectNotFoundTmp', { read: TemplateRef, static: false }) mbsSelectNotFoundTmp: TemplateRef<any>;

  @ContentChildren(NgOptionComponent, { descendants: true }) ngOptions: QueryList<NgOptionComponent>;

  @ViewChild(NgSelectComponent, { static: true }) ngSelect: NgSelectComponent;

  protected labelContentClassesDefault = 'mbs-select_label-content';

  public searchState: SearchState;

  private myValidationWhitespace = false;
  @Input() set validationWhitespace(value: boolean) {
    this.myValidationWhitespace = String(value) !== 'false';
    this.handleWhitespaceValidation(this.myValidationWhitespace);
  }

  public get isAllItemsSelected(): boolean {
    return this.formControlSelect.value?.length === this.items.length;
  }

  public get ngSelectClasses(): string {
    return [
      'mbs-select_ng-select',
      this.isAutoWidth ? 'auto-width' : '',
      this.isCheckboxSelect ? 'checkbox' : '',
      this.size === MbsSize.lg ? 'ng-select-lg' : '',
      this.size === MbsSize.sm ? 'ng-select-sm' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  public get formControlSelect(): FormControl {
    return (this.ngControl.control as FormControl) || new FormControl('', this.ngControl.validator, this.ngControl.asyncValidator);
  }

  private scrollHandler: (this: Window, ev: Event) => unknown;

  private ngbModal = inject(NgbModal);

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true });
    const cd = inject(ChangeDetectorRef);

    super();

    this.cd = cd;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.items?.currentValue && this.selectedItemsFirst) {
      this.sortByAlphabet();
      this.sortBySelection();
    }

    if (this.clearUnavailableSelection) {
      this.handleUnavailableValue();
    }
  }

  ngOnInit(): void {
    if (this.ngControl.valueChanges) {
      this.ngControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.cd.markForCheck());
    }
    if (this.ngControl.statusChanges) {
      this.ngControl.statusChanges.pipe(untilDestroyed(this)).subscribe(() => this.cd.markForCheck());
    }
  }

  ngAfterViewInit(): void {
    this.handleWhitespaceValidation(this.myValidationWhitespace);
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      this.getAppendToElement()?.removeEventListener('scroll', this.scrollHandler);
    }
  }

  ngSelectChangeHandler<T>(event: T): void {
    if (this.onTouched) {
      this.onTouched();
    }

    if (event && this.bindValue) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      this.value = this.multiple && Array.isArray(event) ? event.map((i) => i[this.bindValue]) : event[this.bindValue];
    } else {
      this.value = event;
    }

    this.changeEvent.emit(this.value);
  }

  handleWhitespaceValidation(state: boolean): void {
    if (this.ngControl?.control && state !== false) {
      const extendValidators = Validators.compose([this.ngControl.control.validator, MbsValidators.whitespaceValidator]);
      this.ngControl.control.setValidators(extendValidators);
    }
  }

  handleOpen(): void {
    queueMicrotask(() => this.manageHideOn());

    this.openEvent.emit();
  }

  handleClose(event) {
    if (this.selectedItemsFirst) {
      this.sortByAlphabet();
      this.sortBySelection();
    }

    if (this.isCounterShowing) {
      this.clearSearch();
    }

    this.closeEvent.emit(event);
    this.clearSearch();
  }

  sortBySelection() {
    if (!this.items) return;
    const first = [];
    const last = [];

    this.items.forEach((item) => {
      const value = item[this.bindValue];

      this.value?.includes(value) ? first.push(item) : last.push(item);
    });
    this.items = [...first, ...last];
  }

  sortByAlphabet() {
    if (!this.items) return;
    this.items = this.items.sort((a, b) => a[this.bindLabel].localeCompare(b[this.bindLabel]));

    if (typeof this.groupBy === 'string') {
      this.items.sort((a, b) => a[this.groupBy as string].localeCompare(b[this.groupBy as string]));
    }
  }

  public onSearch(event: { term: string; items: any[] }) {
    this.searchState = event;
  }

  public clearSearch() {
    this.searchState = null;
  }

  public isSomeItemsSelected(group?): boolean {
    const values = this.formControlSelect.value;

    return group
      ? group.children?.some((child) => values?.includes(child.value[this.bindValue || 'value']))
      : values?.length && !this.isAllItemsSelected;
  }

  private manageHideOn(): void {
    if (this.scrollHandler) {
      this.getAppendToElement().removeEventListener('scroll', this.scrollHandler);
    }

    if (!this.hideOn.length) {
      return;
    }

    this.scrollHandler = () => {
      this.hideOn.forEach((strategy) => {
        // eslint-disable-next-line sonarjs/no-small-switch
        switch (strategy) {
          case 'scroll': {
            this.manageHideOnScroll();

            break;
          }
          default:
            break;
        }
      });
    };

    this.getAppendToElement().addEventListener('scroll', this.scrollHandler);
  }

  private manageHideOnScroll(): void {
    if (this.ngSelect.isOpen) {
      this.ngSelect.close();
    }

    if (this.scrollHandler) {
      this.getAppendToElement().removeEventListener('scroll', this.scrollHandler);
    }
  }

  private getAppendToElement(): HTMLElement | null {
    const appendTo = this.appendTo;

    if (!appendTo || appendTo === 'body') {
      return document.body;
    }

    if (appendTo === '.modal-content') {
      return document.querySelector(this.resolvedAppendTo)?.querySelector('.modal-body') || document.body;
    }

    if (typeof appendTo === 'string') {
      return document.querySelector(appendTo);
    }

    return null;
  }

  private handleUnavailableValue() {
    !this?.items?.includes(this.value) && this.ngControl.reset(null);
  }
}
